import React, { useState, useEffect, useRef } from 'react';
import { Input } from '@/components/ui/input';
import { supabase } from '@/integrations/supabase/client';

interface PlaceAutocompleteProps {
  onPlaceSelected: (placeName: string) => void;
  placeholder?: string;
  className?: string;
  initialValue?: string;
}

const GooglePlacesAutocomplete: React.FC<PlaceAutocompleteProps> = ({
  onPlaceSelected,
  placeholder = "Rechercher une ville ou un pays...",
  className = "",
  initialValue = ""
}) => {
  const [inputValue, setInputValue] = useState(initialValue);
  const [suggestions, setSuggestions] = useState<google.maps.places.AutocompletePrediction[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [service, setService] = useState<google.maps.places.AutocompleteService | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout>();

  // Update input value when initialValue changes (for editing)
  useEffect(() => {
    setInputValue(initialValue);
  }, [initialValue]);

  useEffect(() => {
    const initializeGoogleMaps = async () => {
      try {
        // Get API key from Supabase function
        const { data } = await supabase.functions.invoke('get-google-maps-key');
        const apiKey = data?.apiKey;

        if (!apiKey) {
          console.error('No Google Maps API key found');
          return;
        }

        // Load Google Maps API if not already loaded
        if (!window.google?.maps?.places) {
          const script = document.createElement('script');
          script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
          script.async = true;
          script.defer = true;
          
          script.onload = () => {
            if (window.google?.maps?.places) {
              setService(new google.maps.places.AutocompleteService());
            }
          };
          
          document.head.appendChild(script);
        } else {
          setService(new google.maps.places.AutocompleteService());
        }
      } catch (error) {
        console.error('Error initializing Google Maps:', error);
      }
    };

    initializeGoogleMaps();
  }, []);

  const searchPlaces = (query: string) => {
    if (!service || !query.trim()) {
      setSuggestions([]);
      return;
    }

    setIsLoading(true);
    
    service.getPlacePredictions(
      {
        input: query,
        types: ['(regions)'], // Focus on regions/cities/countries
        language: 'fr'
      },
      (predictions, status) => {
        setIsLoading(false);
        
        if (status === google.maps.places.PlacesServiceStatus.OK && predictions) {
          setSuggestions(predictions.slice(0, 5)); // Limit to 5 suggestions
        } else {
          setSuggestions([]);
        }
      }
    );
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputValue(value);
    setShowSuggestions(true);

    // Debounce search
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    timeoutRef.current = setTimeout(() => {
      searchPlaces(value);
    }, 300);
  };

  const handleSuggestionClick = (suggestion: google.maps.places.AutocompletePrediction) => {
    const placeName = suggestion.description;
    setInputValue(placeName);
    setShowSuggestions(false);
    setSuggestions([]);
    onPlaceSelected(placeName);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setShowSuggestions(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (inputRef.current && !inputRef.current.contains(event.target as Node)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return (
    <div className={`relative ${className}`} ref={inputRef}>
      <Input
        value={inputValue}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        className="w-full"
      />
      
      {showSuggestions && suggestions.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-background border border-border rounded-md shadow-lg max-h-60 overflow-y-auto">
          {suggestions.map((suggestion) => (
            <div
              key={suggestion.place_id}
              className="px-4 py-3 cursor-pointer hover:bg-muted transition-colors border-b border-border last:border-b-0"
              onClick={() => handleSuggestionClick(suggestion)}
            >
              <div className="font-medium text-sm">
                {suggestion.structured_formatting.main_text}
              </div>
              {suggestion.structured_formatting.secondary_text && (
                <div className="text-xs text-muted-foreground">
                  {suggestion.structured_formatting.secondary_text}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      
      {isLoading && showSuggestions && (
        <div className="absolute z-50 w-full mt-1 bg-background border border-border rounded-md shadow-lg">
          <div className="px-4 py-3 text-center text-sm text-muted-foreground">
            Recherche en cours...
          </div>
        </div>
      )}
    </div>
  );
};

export default GooglePlacesAutocomplete;